import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Calendar, Clock, MessageSquare, Plus, Trash2, Save, Send, Users, ShieldCheck, Smartphone, Image, Eye, ExternalLink, Globe, Layout, RefreshCw, Search, RotateCcw, MousePointerClick, Edit3, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, themes } from '../ThemeProvider';
import { API_URL } from '../../lib/api-client';

interface CampaignSettings {
  enabled: boolean;
  scheduledDates: string[];
  cooldownHours: number;
  maxRecipientsPerBatch: number;
  frequencyPerDay: number;
  includeProfileLink: boolean;
  stopModalIfDispatchedToday: boolean;
  stopAfterDispatches: number;
  autoSendDelaySeconds: number;
  enableAutoSendTimer: boolean;
  messageTemplate: string;
  showOnlyToPrepaidUsers: boolean;
  minInstallAgeMinutes: number;
  requireSmsPermission: boolean;
  totalMaxViews: number;
  totalMaxDispatches: number;
  restrictedUserIds: string[];
  restrictedAppVersion: string;
}

interface BannerItem {
  _id?: string;
  enabled: boolean;
  title: string;
  description: string;
  imageUrl: string;
  linkType: 'webview' | 'browser' | 'screen';
  linkUrl: string;
  buttonText: string;
  maxViewsPerDevice: number;
  maxClicksPerDevice: number;
  maxViewsPerDevicePerDay: number;
  maxClicksPerDevicePerDay: number;
  totalMaxViews: number;
  totalMaxClicks: number;
  status: 'active' | 'paused' | 'completed';
  isCompleted?: boolean;
  priority: number;
  stats?: {
    totalViews: number;
    totalClicks: number;
    uniqueDevicesCount: number;
    ctr: string;
  };
}

interface DeviceTrackItem {
  _id: string;
  deviceId: string;
  campaignTitle?: string;
  viewsCount: number;
  clicksCount: number;
  dailyViews?: number;
  dailyClicks?: number;
  maxViews?: number;
  maxClicks?: number;
  maxDailyViews?: number;
  maxDailyClicks?: number;
  isLimitReached: boolean;
  statusText: string;
  lastViewedAt: string | null;
  lastClickedAt: string | null;
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

interface DeviceStatsSummary {
  totalDevices: number;
  overallViews: number;
  overallClicks: number;
  ctr: string;
}

interface SimStats {
  prepaid: number;
  postpaid: number;
  unknown: number;
  total: number;
}

const emptyBannerForm: BannerItem = {
  enabled: true,
  title: '',
  description: '',
  imageUrl: '',
  linkType: 'screen',
  linkUrl: 'Plans',
  buttonText: 'View Offer',
  maxViewsPerDevice: 10,
  maxClicksPerDevice: 2,
  maxViewsPerDevicePerDay: 3,
  maxClicksPerDevicePerDay: 2,
  totalMaxViews: 100000,
  totalMaxClicks: 5000,
  status: 'active',
  priority: 0
};

export function SmsCampaignSettingsAdmin() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  const [activeTab, setActiveTab] = useState<'sms' | 'banner'>('sms');

  // SMS Campaign state
  const [settings, setSettings] = useState<CampaignSettings>({
    enabled: true,
    scheduledDates: [],
    cooldownHours: 105,
    maxRecipientsPerBatch: 70,
    frequencyPerDay: 1,
    includeProfileLink: true,
    stopModalIfDispatchedToday: true,
    stopAfterDispatches: 2,
    autoSendDelaySeconds: 2,
    enableAutoSendTimer: true,
    messageTemplate: 'Find your perfect life partner today on our app! Join now: https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp',
    showOnlyToPrepaidUsers: false,
    minInstallAgeMinutes: 0,
    requireSmsPermission: false,
    totalMaxViews: 100000,
    totalMaxDispatches: 100000,
    restrictedUserIds: [],
    restrictedAppVersion: '',
  });

  const [newDate, setNewDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [simStats, setSimStats] = useState<SimStats | null>(null);

  // Multi-Banner Campaign state
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [fetchingBanners, setFetchingBanners] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerItem | null>(null);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleUploadBannerImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image file size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setUploadingImage(true);
        const base64 = reader.result as string;
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/banner-campaign/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ image: base64 })
        });
        const data = await res.json();
        if (data.success && data.url) {
          toast.success('Banner image uploaded to AWS S3!');
          setEditingBanner(prev => prev ? ({ ...prev, imageUrl: data.url }) : null);
        } else {
          toast.error(data.message || 'Failed to upload image to AWS S3');
        }
      } catch (err) {
        console.error('Image upload error:', err);
        toast.error('Error uploading image to AWS S3');
      } finally {
        setUploadingImage(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Per-Device stats state
  const [deviceTracks, setDeviceTracks] = useState<DeviceTrackItem[]>([]);
  const [statsSummary, setStatsSummary] = useState<DeviceStatsSummary>({
    totalDevices: 0,
    overallViews: 0,
    overallClicks: 0,
    ctr: '0%'
  });
  const [searchDevice, setSearchDevice] = useState('');
  const [fetchingStats, setFetchingStats] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSimStats();
    fetchBannerCampaigns();
    fetchDeviceStats();
  }, []);

  const fetchSettings = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/sms-campaign/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setSettings({
          enabled: data.data.enabled ?? true,
          scheduledDates: data.data.scheduledDates || [],
          cooldownHours: data.data.cooldownHours || 105,
          maxRecipientsPerBatch: data.data.maxRecipientsPerBatch || 70,
          frequencyPerDay: data.data.frequencyPerDay || 1,
          includeProfileLink: data.data.includeProfileLink !== false,
          stopModalIfDispatchedToday: data.data.stopModalIfDispatchedToday !== false,
          stopAfterDispatches: typeof data.data.stopAfterDispatches === 'number' ? data.data.stopAfterDispatches : 2,
          autoSendDelaySeconds: typeof data.data.autoSendDelaySeconds === 'number' ? data.data.autoSendDelaySeconds : 2,
          enableAutoSendTimer: data.data.enableAutoSendTimer !== false,
          messageTemplate: data.data.messageTemplate || '',
          showOnlyToPrepaidUsers: data.data.showOnlyToPrepaidUsers === true,
          minInstallAgeMinutes: typeof data.data.minInstallAgeMinutes === 'number' ? data.data.minInstallAgeMinutes : 0,
          requireSmsPermission: data.data.requireSmsPermission === true,
          totalMaxViews: typeof data.data.totalMaxViews === 'number' ? data.data.totalMaxViews : 100000,
          totalMaxDispatches: typeof data.data.totalMaxDispatches === 'number' ? data.data.totalMaxDispatches : 100000,
          restrictedUserIds: Array.isArray(data.data.restrictedUserIds) ? data.data.restrictedUserIds : [],
          restrictedAppVersion: typeof data.data.restrictedAppVersion === 'string' ? data.data.restrictedAppVersion : '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch SMS campaign settings:', err);
    } finally {
      setFetching(false);
    }
  };

  const fetchSimStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/sim-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setSimStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch SIM stats:', err);
    }
  };

  const fetchBannerCampaigns = async () => {
    try {
      setFetchingBanners(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/banner-campaign/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setBanners(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch Banner campaigns list:', err);
    } finally {
      setFetchingBanners(false);
    }
  };

  const fetchDeviceStats = async (query = searchDevice) => {
    try {
      setFetchingStats(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/banner-campaign/device-stats?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setDeviceTracks(data.data.devices || []);
        setStatsSummary(data.data.summary || {
          totalDevices: 0,
          overallViews: 0,
          overallClicks: 0,
          ctr: '0%'
        });
      }
    } catch (err) {
      console.error('Failed to fetch Banner device stats:', err);
    } finally {
      setFetchingStats(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/sms-campaign/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('SMS Campaign settings saved successfully');
      } else {
        toast.error(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Failed to save campaign settings:', err);
      toast.error('An error occurred while saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBanner = async (bannerData: BannerItem) => {
    if (!bannerData.title || !bannerData.imageUrl) {
      toast.error('Campaign Title and Image URL are required');
      return;
    }

    try {
      setSavingBanner(true);
      const token = localStorage.getItem('token');
      const isEdit = Boolean(bannerData._id);
      const url = isEdit
        ? `${API_URL}/banner-campaign/${bannerData._id}`
        : `${API_URL}/banner-campaign`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(bannerData)
      });

      const data = await res.json();
      if (data.success) {
        toast.success(isEdit ? 'Banner campaign updated!' : 'New banner campaign created!');
        setShowBannerForm(false);
        setEditingBanner(null);
        fetchBannerCampaigns();
        fetchDeviceStats();
      } else {
        toast.error(data.message || 'Failed to save banner campaign');
      }
    } catch (err) {
      console.error('Failed to save banner campaign:', err);
      toast.error('Error saving banner campaign');
    } finally {
      setSavingBanner(false);
    }
  };

  const handleToggleBannerEnabled = async (banner: BannerItem) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/banner-campaign/${banner._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: !banner.enabled })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Banner "${banner.title}" is now ${!banner.enabled ? 'Enabled' : 'Disabled'}`);
        fetchBannerCampaigns();
      }
    } catch (err) {
      console.error('Failed to toggle banner status:', err);
    }
  };

  const handleDeleteBanner = async (id: string, title: string) => {
    if (!confirm(`Are you sure you want to delete the banner campaign "${title}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/banner-campaign/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Banner campaign deleted');
        fetchBannerCampaigns();
        fetchDeviceStats();
      } else {
        toast.error(data.message || 'Failed to delete banner campaign');
      }
    } catch (err) {
      console.error('Failed to delete banner campaign:', err);
    }
  };

  const handleResetDeviceStats = async (deviceId?: string, campaignId?: string, clearAll = false) => {
    if (clearAll && !confirm('Are you sure you want to reset ALL device view and click stats?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/banner-campaign/reset-device-stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ deviceId, campaignId, clearAll })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Device stats reset successfully');
        fetchDeviceStats();
      } else {
        toast.error(data.message || 'Failed to reset device stats');
      }
    } catch (err) {
      console.error('Failed to reset device stats:', err);
      toast.error('Error resetting device stats');
    }
  };

  const handleAddDate = () => {
    if (!newDate) return;
    if (settings.scheduledDates.includes(newDate)) {
      toast.error('Date is already added');
      return;
    }
    setSettings(prev => ({
      ...prev,
      scheduledDates: [...prev.scheduledDates, newDate].sort()
    }));
    setNewDate('');
  };

  const handleRemoveDate = (dateToRemove: string) => {
    setSettings(prev => ({
      ...prev,
      scheduledDates: prev.scheduledDates.filter(d => d !== dateToRemove)
    }));
  };

  if (fetching && fetchingBanners) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          Loading Campaign Engine Settings...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Module Navigation Tabs */}
      <div className="flex border-b border-gray-200 gap-2">
        <button
          onClick={() => setActiveTab('sms')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'sms'
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50 rounded-t-lg'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Send className="h-4 w-4" />
          SMS Campaign & Cooldown
        </button>
        <button
          onClick={() => setActiveTab('banner')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'banner'
              ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50 rounded-t-lg'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Image className="h-4 w-4 text-purple-600" />
          Banner Show Modal Campaign
          <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0 border-purple-200">
            Multi-Banner Listing
          </Badge>
        </button>
      </div>

      {/* TAB 1: SMS CAMPAIGN SETTINGS */}
      {activeTab === 'sms' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
              <Send className="h-4 w-4 md:h-5 md:w-5" style={{ color: currentTheme.primary }} />
              SMS Campaign & Cooldown Engine
            </CardTitle>
            <CardDescription className="text-xs">
              Configure target dates, daily frequency, global deduplication, and recipient cooldown rules.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* ── Campaign Settings Tile Grid ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2">

              {/* Tile 1: Enable Campaign */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.enabled ? 'bg-green-50 border-green-500 shadow-xs' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}>
                <span className="text-lg">🚀</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Enable Campaign</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.enabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {settings.enabled ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Tile 2: Include Profile Link */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.includeProfileLink ? 'bg-blue-50 border-blue-500 shadow-xs' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSettings(prev => ({ ...prev, includeProfileLink: !prev.includeProfileLink }))}>
                <span className="text-lg">🔗</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Profile Deep Link</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.includeProfileLink ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {settings.includeProfileLink ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Tile 3: SIM Detection / Prepaid Only */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.showOnlyToPrepaidUsers ? 'bg-orange-50 border-orange-500 shadow-xs' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSettings(prev => ({ ...prev, showOnlyToPrepaidUsers: !prev.showOnlyToPrepaidUsers }))}>
                <span className="text-lg">📶</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Prepaid SIM Only</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.showOnlyToPrepaidUsers ? 'bg-orange-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {settings.showOnlyToPrepaidUsers ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Tile 4: Stop Modal If Dispatched Today */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.stopModalIfDispatchedToday ? 'bg-red-50 border-red-500 shadow-xs' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSettings(prev => ({ ...prev, stopModalIfDispatchedToday: !prev.stopModalIfDispatchedToday }))}>
                <span className="text-lg">🚫</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Stop If Sent Today</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.stopModalIfDispatchedToday ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {settings.stopModalIfDispatchedToday ? 'ON' : 'OFF'}
                </span>
              </div>

              {/* Tile 5: Auto Send Timer */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.enableAutoSendTimer ? 'bg-purple-50 border-purple-500 shadow-xs' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSettings(prev => ({ ...prev, enableAutoSendTimer: !prev.enableAutoSendTimer }))}>
                <span className="text-lg">⏱️</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Auto-Send Timer</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.enableAutoSendTimer ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {settings.enableAutoSendTimer ? `${settings.autoSendDelaySeconds}s` : 'OFF'}
                </span>
              </div>

              {/* Tile 6: Direct SMS Permission Mode */}
              <div className={`flex flex-col items-center justify-center gap-1 p-2 rounded-lg border cursor-pointer transition-all select-none ${settings.requireSmsPermission ? 'bg-teal-50 border-teal-500 shadow-xs' : 'bg-emerald-50 border-emerald-500 shadow-xs'}`}
                onClick={() => setSettings(prev => ({ ...prev, requireSmsPermission: !prev.requireSmsPermission }))}>
                <span className="text-lg">{settings.requireSmsPermission ? '🔒' : '💬'}</span>
                <span className="text-[11px] font-bold text-center text-gray-800 leading-tight">Require SMS Permission</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${settings.requireSmsPermission ? 'bg-teal-600 text-white' : 'bg-emerald-600 text-white'}`}>
                  {settings.requireSmsPermission ? 'ON (Background)' : 'OFF (Default SMS App)'}
                </span>
              </div>

            </div>

            {/* ── Numeric Settings Grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mt-2">

              {/* Card: Stop Modal After Dispatches Today */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">🛑</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Stop Modal After Dispatches Today</div>
                    <div className="text-xs text-gray-500">Hide modal after N sends today</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[1, 2, 3, 5, 10, 50].map(num => (
                    <button key={num} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, stopAfterDispatches: num }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.stopAfterDispatches === num ? 'bg-red-500 text-white border-red-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {num}x
                    </button>
                  ))}
                  <input type="number" min={1} max={1000000}
                    value={settings.stopAfterDispatches}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, stopAfterDispatches: v })); }}
                    className="w-16 h-6 text-center text-xs font-bold border border-red-300 rounded bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-400" />
                </div>
              </div>

              {/* Card: Daily Modal View Frequency */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">👁️</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Daily Modal Open Frequency</div>
                    <div className="text-xs text-gray-500">Max times modal opens per day</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[1, 2, 3, 5, 10, 50, 1000].map(num => (
                    <button key={num} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, frequencyPerDay: num }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.frequencyPerDay === num ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {num === 1000 ? '1K' : `${num}x`}
                    </button>
                  ))}
                  <input type="number" min={1} max={10000000}
                    value={settings.frequencyPerDay}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, frequencyPerDay: v })); }}
                    className="w-20 h-6 text-center text-xs font-bold border border-indigo-300 rounded bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    placeholder="Custom" />
                </div>
              </div>

              {/* Card: Lifetime Total Modal Open Limit (Per Device) */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">🎯</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Total Lifetime Open Limit (Per Device)</div>
                    <div className="text-xs text-gray-500">Stop modal after N opens lifetime</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[1, 2, 5, 10, 50, 100, 100000].map(num => (
                    <button key={num} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, totalMaxViews: num }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.totalMaxViews === num ? 'bg-indigo-700 text-white border-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {num === 100000 ? '∞' : `${num}`}
                    </button>
                  ))}
                  <input type="number" min={1} max={10000000}
                    value={settings.totalMaxViews}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, totalMaxViews: v })); }}
                    className="w-20 h-6 text-center text-xs font-bold border border-indigo-400 rounded bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Custom" />
                </div>
              </div>

              {/* Card: Lifetime Total Dispatches Limit (Per Device) */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">🏁</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Total Lifetime Dispatch Limit (Per Device)</div>
                    <div className="text-xs text-gray-500">Stop modal after N dispatches lifetime</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[1, 2, 5, 10, 50, 1000, 100000].map(num => (
                    <button key={num} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, totalMaxDispatches: num }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.totalMaxDispatches === num ? 'bg-red-700 text-white border-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {num === 100000 ? '∞' : `${num}`}
                    </button>
                  ))}
                  <input type="number" min={1} max={10000000}
                    value={settings.totalMaxDispatches}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, totalMaxDispatches: v })); }}
                    className="w-20 h-6 text-center text-xs font-bold border border-red-400 rounded bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-500"
                    placeholder="Custom" />
                </div>
              </div>

              {/* Card: Restricted Users (IDs / Emails / Phones) */}
              <div className="border rounded-xl p-3 bg-white space-y-2 sm:col-span-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">🚫</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Restricted Users (User IDs / Emails / Phones)</div>
                    <div className="text-xs text-gray-500">Modal will NOT open for users listed here (comma-separated)</div>
                  </div>
                </div>
                <input
                  type="text"
                  value={settings.restrictedUserIds.join(', ')}
                  onChange={e => {
                    const list = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    setSettings(prev => ({ ...prev, restrictedUserIds: list }));
                  }}
                  className="w-full h-8 px-2.5 text-xs border border-red-200 rounded bg-red-50/50 focus:outline-none focus:ring-1 focus:ring-red-400 font-mono"
                  placeholder="e.g. 60d21b4667d0d8992e610c85, test@user.com, 9876543210"
                />
              </div>

              {/* Card: Restricted App Versions */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">📲</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Restricted App Versions</div>
                    <div className="text-xs text-gray-500">App versions where modal is BLOCKED (comma-separated)</div>
                  </div>
                </div>
                <input
                  type="text"
                  value={settings.restrictedAppVersion}
                  onChange={e => {
                    setSettings(prev => ({ ...prev, restrictedAppVersion: e.target.value }));
                  }}
                  className="w-full h-8 px-2.5 text-xs border border-indigo-200 rounded bg-indigo-50/50 focus:outline-none focus:ring-1 focus:ring-indigo-400 font-mono"
                  placeholder="e.g. 1.0.0, 1.0.1"
                />
              </div>

              {/* Card: Auto Send Timer Seconds */}
              {settings.enableAutoSendTimer && (
                <div className="border rounded-xl p-3 bg-white space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base">⏱️</span>
                    <div>
                      <div className="text-xs font-bold text-gray-800">Auto-Send Delay (Seconds)</div>
                      <div className="text-xs text-gray-500">Countdown before auto-dispatching</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {[0, 1, 2, 3, 5, 10].map(sec => (
                      <button key={sec} type="button"
                        onClick={() => setSettings(prev => ({ ...prev, autoSendDelaySeconds: sec }))}
                        className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.autoSendDelaySeconds === sec ? 'bg-purple-600 text-white border-purple-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                        {sec === 0 ? 'Off' : `${sec}s`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Card: Global Cooldown Hours */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">⏳</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Global Recipient Cooldown</div>
                    <div className="text-xs text-gray-500">Hours before same contact re-receives</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[24, 48, 72, 168, 720, 100000].map(h => (
                    <button key={h} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, cooldownHours: h }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.cooldownHours === h ? 'bg-amber-500 text-white border-amber-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {h === 100000 ? '∞' : h === 720 ? '30d' : h === 168 ? '7d' : `${h}h`}
                    </button>
                  ))}
                  <input type="number" min={1} max={1000000}
                    value={settings.cooldownHours}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, cooldownHours: v })); }}
                    className="w-16 h-6 text-center text-xs font-bold border border-amber-300 rounded bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400" />
                  <span className="text-xs text-gray-500 font-medium">hrs</span>
                </div>
              </div>

              {/* Card: Max Recipients Per Batch */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">📋</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Max Recipients Per Batch</div>
                    <div className="text-xs text-gray-500">Contacts picked per send batch</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[20, 50, 70, 100, 150, 200].map(cnt => (
                    <button key={cnt} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, maxRecipientsPerBatch: cnt }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.maxRecipientsPerBatch === cnt ? 'bg-purple-600 text-white border-purple-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {cnt}
                    </button>
                  ))}
                  <input type="number" min={1} max={10000}
                    value={settings.maxRecipientsPerBatch}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 1) setSettings(prev => ({ ...prev, maxRecipientsPerBatch: v })); }}
                    className="w-16 h-6 text-center text-xs font-bold border border-purple-300 rounded bg-purple-50 focus:outline-none focus:ring-1 focus:ring-purple-400" />
                </div>
              </div>

              {/* Card: Min Install Age */}
              <div className="border rounded-xl p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">📲</span>
                  <div>
                    <div className="text-xs font-bold text-gray-800">Min Install Age (Minutes)</div>
                    <div className="text-xs text-gray-500">Wait after app install before showing</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[0, 5, 15, 30, 60, 120].map(m => (
                    <button key={m} type="button"
                      onClick={() => setSettings(prev => ({ ...prev, minInstallAgeMinutes: m }))}
                      className={`px-2 py-0.5 text-xs font-bold rounded border transition-colors ${settings.minInstallAgeMinutes === m ? 'bg-teal-600 text-white border-teal-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'}`}>
                      {m === 0 ? 'Off' : `${m}m`}
                    </button>
                  ))}
                  <input type="number" min={0} max={100000}
                    value={settings.minInstallAgeMinutes}
                    onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) setSettings(prev => ({ ...prev, minInstallAgeMinutes: v })); }}
                    className="w-16 h-6 text-center text-xs font-bold border border-teal-300 rounded bg-teal-50 focus:outline-none focus:ring-1 focus:ring-teal-400" />
                </div>
              </div>

            </div>


            {/* Scheduled Dates Section */}
            <div className="p-4 border rounded-lg space-y-3 bg-gray-50/50">
              <Label className="flex items-center gap-2 font-semibold">
                <Calendar className="h-4 w-4 text-indigo-600" />
                Scheduled Campaign Target Dates
              </Label>
              <p className="text-xs text-gray-500">
                Database is checked ONLY on these specific target dates. Zero DB overhead on non-scheduled days.
              </p>

              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  className="max-w-xs"
                />
                <Button type="button" onClick={handleAddDate} className="flex items-center gap-1">
                  <Plus className="h-4 w-4" /> Add Target Date
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {settings.scheduledDates.length === 0 ? (
                  <span className="text-sm text-gray-400 italic">No scheduled dates added yet.</span>
                ) : (
                  settings.scheduledDates.map(dateStr => (
                    <Badge key={dateStr} variant="outline" className="px-3 py-1 text-sm flex items-center gap-2 bg-indigo-50 border-indigo-200">
                      <span>{dateStr}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDate(dateStr)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* SMS Message Template */}
            <div className="p-4 border rounded-lg space-y-2">
              <Label className="flex items-center gap-2 font-semibold">
                <MessageSquare className="h-4 w-4 text-purple-500" />
                SMS Campaign Message Template
              </Label>
              <textarea
                className="w-full h-24 p-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={settings.messageTemplate}
                onChange={e => setSettings(prev => ({ ...prev, messageTemplate: e.target.value }))}
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={loading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              >
                <Save className="h-4 w-4" /> Save Campaign Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TAB 2: MULTI-BANNER SHOW MODAL CAMPAIGNS */}
      {activeTab === 'banner' && (
        <div className="space-y-6">
          {/* Card 1: Banner Campaign Listing Header & Action */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                    <Image className="h-5 w-5 text-purple-600" />
                    Banner Show Modal Campaigns ({banners.length})
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Add multiple banner campaigns. The mobile app will show <strong>1 eligible banner</strong> per app launch based on each device's view and click limits.
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setEditingBanner({ ...emptyBannerForm });
                    setShowBannerForm(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2 text-xs"
                >
                  <Plus className="h-4 w-4" /> Add New Banner Campaign
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Inline / Modal Create & Edit Form */}
              {showBannerForm && editingBanner && (
                <div className="p-4 border-2 border-purple-300 rounded-xl bg-purple-50/40 space-y-4 relative">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="font-bold text-sm text-purple-900 flex items-center gap-2">
                      <Image className="h-4 w-4 text-purple-600" />
                      {editingBanner._id ? 'Edit Banner Campaign' : 'Create New Banner Campaign'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowBannerForm(false);
                        setEditingBanner(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title */}
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Campaign Title *</Label>
                      <Input
                        value={editingBanner.title}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, title: e.target.value }) : null)}
                        placeholder="e.g. Special Membership Discount!"
                        className="bg-white"
                      />
                    </div>

                    {/* Action Button Text */}
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Button Text</Label>
                      <Input
                        value={editingBanner.buttonText}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, buttonText: e.target.value }) : null)}
                        placeholder="e.g. View Offer / Upgrade Now"
                        className="bg-white"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Description</Label>
                    <textarea
                      className="w-full h-16 p-2.5 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={editingBanner.description}
                      onChange={e => setEditingBanner(prev => prev ? ({ ...prev, description: e.target.value }) : null)}
                      placeholder="Short offer details text shown under the banner..."
                    />
                  </div>

                  {/* Image URL & Preview */}
                  <div className="space-y-1.5 bg-white p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold flex items-center gap-1.5">
                        <Image className="h-4 w-4 text-purple-600" />
                        Banner Image URL *
                      </Label>
                      <label className="bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-300 font-semibold px-3 py-1 text-xs rounded-md cursor-pointer flex items-center gap-1.5 transition-colors">
                        {uploadingImage ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin text-purple-600" />
                            Uploading to AWS S3...
                          </>
                        ) : (
                          <>
                            <span>☁️</span> Upload File to AWS S3
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleUploadBannerImage}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <div className="flex gap-3 items-center pt-1">
                      <Input
                        value={editingBanner.imageUrl}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, imageUrl: e.target.value }) : null)}
                        placeholder="Paste image URL or click 'Upload File to AWS S3' above"
                        className="flex-1"
                      />
                      {editingBanner.imageUrl && (
                        <div className="w-16 h-12 rounded overflow-hidden border bg-gray-100 flex-shrink-0">
                          <img
                            src={editingBanner.imageUrl}
                            alt="Banner Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Type & Target */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-3 border rounded-lg">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold flex items-center gap-1.5">
                        <Globe className="h-4 w-4 text-indigo-600" />
                        Click Action Type
                      </Label>
                      <select
                        className="w-full p-2 border rounded-md text-sm bg-white font-medium focus:ring-2 focus:ring-indigo-500"
                        value={editingBanner.linkType}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, linkType: e.target.value as any }) : null)}
                      >
                        <option value="screen">📱 In-App Screen (e.g. Payment Plans / Membership)</option>
                        <option value="webview">🌐 In-App WebView (Open web link inside app)</option>
                        <option value="browser">🔗 External Browser (Open link in default browser)</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold flex items-center gap-1.5">
                        <ExternalLink className="h-4 w-4 text-indigo-600" />
                        {editingBanner.linkType === 'screen' ? 'Select Target In-App Screen' : 'Target Web Link URL'}
                      </Label>
                      {editingBanner.linkType === 'screen' ? (
                        <select
                          className="w-full p-2 border rounded-md text-sm bg-white font-medium focus:ring-2 focus:ring-indigo-500"
                          value={editingBanner.linkUrl}
                          onChange={e => setEditingBanner(prev => prev ? ({ ...prev, linkUrl: e.target.value }) : null)}
                        >
                          <option value="Plans">💎 Payment Plans & Premium Membership (Plans)</option>
                          <option value="Profile">👤 My Profile (Profile)</option>
                          <option value="edit-profile">✏️ Edit Profile Page (edit-profile)</option>
                          <option value="search">🔍 Search Matches & Profiles (search)</option>
                          <option value="filters">🎛️ Advanced Search Filters (filters)</option>
                          <option value="chat">💬 Messages & Chat (chat)</option>
                          <option value="shortlisted">⭐ Shortlisted & Saved Profiles (shortlisted)</option>
                          <option value="notifications">🔔 Notifications (notifications)</option>
                          <option value="refer">🎁 Refer & Earn Rewards (refer)</option>
                          <option value="settings">⚙️ Account Settings (settings)</option>
                          <option value="kundli">🔮 Kundli & Horoscope Matching (kundli)</option>
                        </select>
                      ) : (
                        <Input
                          value={editingBanner.linkUrl}
                          onChange={e => setEditingBanner(prev => prev ? ({ ...prev, linkUrl: e.target.value }) : null)}
                          placeholder="https://example.com/checkout"
                        />
                      )}
                    </div>
                  </div>

                  {/* Per-Device & Daily Impression Target Rules */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white p-3 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5 text-blue-600" />
                        Max Views / Device (Total)
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={editingBanner.maxViewsPerDevice}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, maxViewsPerDevice: Number(e.target.value) || 10 }) : null)}
                        className="text-center font-bold"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 text-purple-600" />
                        Max Views / Device / Day
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={editingBanner.maxViewsPerDevicePerDay}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, maxViewsPerDevicePerDay: Number(e.target.value) || 3 }) : null)}
                        className="text-center font-bold"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <MousePointerClick className="h-3.5 w-3.5 text-green-600" />
                        Max Clicks / Device (Total)
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={editingBanner.maxClicksPerDevice}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, maxClicksPerDevice: Number(e.target.value) || 2 }) : null)}
                        className="text-center font-bold"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 text-amber-600" />
                        Max Clicks / Device / Day
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        value={editingBanner.maxClicksPerDevicePerDay}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, maxClicksPerDevicePerDay: Number(e.target.value) || 2 }) : null)}
                        className="text-center font-bold"
                      />
                    </div>
                  </div>

                  {/* Global Campaign Overall Targets */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <Users className="h-3.5 w-3.5 text-indigo-600" />
                        Global Target Views (All Devices)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        value={editingBanner.totalMaxViews}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, totalMaxViews: Number(e.target.value) || 0 }) : null)}
                        placeholder="100000 (0 = unlimited)"
                        className="text-center font-bold"
                      />
                      <span className="text-[10px] text-gray-400 block">Auto-deactivates when reached</span>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        <MousePointerClick className="h-3.5 w-3.5 text-amber-600" />
                        Global Target Clicks (All Devices)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        value={editingBanner.totalMaxClicks}
                        onChange={e => setEditingBanner(prev => prev ? ({ ...prev, totalMaxClicks: Number(e.target.value) || 0 }) : null)}
                        placeholder="5000 (0 = unlimited)"
                        className="text-center font-bold"
                      />
                      <span className="text-[10px] text-gray-400 block">Auto-deactivates when reached</span>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-bold text-gray-800 flex items-center gap-1">
                        Campaign Active Status
                      </Label>
                      <div className="flex items-center gap-3 pt-2">
                        <Switch
                          checked={editingBanner.enabled}
                          onCheckedChange={val => setEditingBanner(prev => prev ? ({ ...prev, enabled: val, status: val ? 'active' : 'paused' }) : null)}
                        />
                        <span className="text-xs font-semibold">{editingBanner.enabled ? '🟢 Active' : '⏸️ Paused'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowBannerForm(false);
                        setEditingBanner(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => editingBanner && handleSaveBanner(editingBanner)}
                      disabled={savingBanner}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1"
                    >
                      <Save className="h-4 w-4" /> Save Campaign
                    </Button>
                  </div>
                </div>
              )}

              {/* Banners List Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-100 text-gray-700 font-semibold border-b">
                    <tr>
                      <th className="p-3">Banner</th>
                      <th className="p-3">Title & Details</th>
                      <th className="p-3">Action Target</th>
                      <th className="p-3 text-center">Per-Device Limits</th>
                      <th className="p-3 text-center">Stats (Views / Clicks)</th>
                      <th className="p-3 text-center">Status</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y text-gray-700">
                    {fetchingBanners ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-gray-500">
                          Loading banner campaigns list...
                        </td>
                      </tr>
                    ) : banners.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-400 italic">
                          No banner campaigns created yet. Click "+ Add New Banner Campaign" above to create your first banner!
                        </td>
                      </tr>
                    ) : (
                      banners.map(banner => (
                        <tr key={banner._id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="w-16 h-12 rounded overflow-hidden border bg-gray-100">
                              <img
                                src={banner.imageUrl}
                                alt={banner.title}
                                className="w-full h-full object-cover"
                                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                              />
                            </div>
                          </td>
                          <td className="p-3 max-w-[200px]">
                            <span className="font-bold text-gray-900 block text-sm">{banner.title}</span>
                            {banner.description ? (
                              <span className="text-gray-500 text-[11px] line-clamp-1">{banner.description}</span>
                            ) : null}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Badge variant="outline" className="capitalize text-[10px] bg-indigo-50 border-indigo-200">
                                {banner.linkType}
                              </Badge>
                            </div>
                            <span className="font-mono text-[11px] text-gray-600 block truncate max-w-[150px]">
                              {banner.linkUrl}
                            </span>
                          </td>
                          <td className="p-3 text-center space-y-0.5">
                            <span className="block font-semibold text-blue-700 text-[11px]">👁️ Total: Max {banner.maxViewsPerDevice} Views | {banner.maxClicksPerDevice} Clicks</span>
                            <span className="block font-semibold text-purple-700 text-[11px]">📅 Today: Max {banner.maxViewsPerDevicePerDay || 3} Views/day | {banner.maxClicksPerDevicePerDay || 2} Clicks/day</span>
                          </td>
                          <td className="p-3 text-center">
                            <div className="font-bold text-gray-800">
                              {banner.stats?.totalViews || 0} / {banner.totalMaxViews || 100000} Views
                            </div>
                            <div className="text-[11px] text-gray-600">
                              {banner.stats?.totalClicks || 0} / {banner.totalMaxClicks || 5000} Clicks | <span className="text-amber-600 font-semibold">CTR: {banner.stats?.ctr || '0%'}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            {(banner.isCompleted || banner.status === 'completed') ? (
                              <span className="px-2.5 py-1 bg-green-100 text-green-800 text-[11px] font-bold rounded-full border border-green-300 shadow-sm inline-block">
                                🎉 Target Completed
                              </span>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <Switch
                                  checked={banner.enabled}
                                  onCheckedChange={() => handleToggleBannerEnabled(banner)}
                                />
                                <span className={`text-[10px] font-bold ${banner.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                                  {banner.enabled ? '🟢 Active' : '⏸️ Paused'}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingBanner({ ...banner });
                                  setShowBannerForm(true);
                                }}
                                className="text-indigo-600 hover:bg-indigo-50"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => banner._id && handleDeleteBanner(banner._id, banner.title)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Per-Device Impressions & Clicks Tracking Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-indigo-600" />
                    Per-Device Views & Clicks Tracking Log
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Live impression records per device against each campaign threshold.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchDeviceStats()}
                    className="flex items-center gap-1 text-xs"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetDeviceStats(undefined, undefined, true)}
                    className="flex items-center gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Clear All Device Stats
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-center">
                  <span className="text-xs text-blue-700 font-medium block">Total Unique Devices</span>
                  <span className="text-xl font-bold text-blue-900">{statsSummary.totalDevices}</span>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-center">
                  <span className="text-xs text-purple-700 font-medium block">Total Impressions/Views</span>
                  <span className="text-xl font-bold text-purple-900">{statsSummary.overallViews}</span>
                </div>
                <div className="p-3 bg-green-50 border border-green-100 rounded-lg text-center">
                  <span className="text-xs text-green-700 font-medium block">Total Clicks</span>
                  <span className="text-xl font-bold text-green-900">{statsSummary.overallClicks}</span>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-center">
                  <span className="text-xs text-amber-700 font-medium block">Average CTR</span>
                  <span className="text-xl font-bold text-amber-900">{statsSummary.ctr}</span>
                </div>
              </div>

              {/* Search Bar */}
              <div className="flex gap-2 max-w-md">
                <Input
                  placeholder="Search device ID..."
                  value={searchDevice}
                  onChange={e => {
                    setSearchDevice(e.target.value);
                    fetchDeviceStats(e.target.value);
                  }}
                  className="text-xs"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchDeviceStats()}
                  className="flex items-center gap-1"
                >
                  <Search className="h-3.5 w-3.5" /> Search
                </Button>
              </div>

              {/* Device Tracks Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-100 text-gray-700 font-semibold border-b">
                    <tr>
                      <th className="p-3">Device ID</th>
                      <th className="p-3">Campaign Title</th>
                      <th className="p-3">User (If Logged In)</th>
                      <th className="p-3 text-center">Views Count</th>
                      <th className="p-3 text-center">Clicks Count</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y text-gray-700">
                    {fetchingStats ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-gray-500">
                          Loading device tracking stats...
                        </td>
                      </tr>
                    ) : deviceTracks.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-gray-400 italic">
                          No device views or clicks recorded yet.
                        </td>
                      </tr>
                    ) : (
                      deviceTracks.map(item => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="p-3 font-mono font-semibold text-gray-800">
                            {item.deviceId}
                          </td>
                          <td className="p-3 font-medium text-purple-900">
                            {item.campaignTitle}
                          </td>
                          <td className="p-3">
                            {item.userId ? (
                              <div>
                                <span className="font-medium text-gray-900 block">
                                  {item.userId.firstName} {item.userId.lastName}
                                </span>
                                <span className="text-[10px] text-gray-500">{item.userId.email || item.userId.phone}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Guest Device</span>
                            )}
                          </td>
                          <td className="p-3 text-center font-bold text-blue-600">
                            {item.viewsCount} / {item.maxViews || 10}
                          </td>
                          <td className="p-3 text-center font-bold text-green-600">
                            {item.clicksCount} / {item.maxClicks || 2}
                          </td>
                          <td className="p-3">
                            <Badge
                              variant="outline"
                              className={
                                item.isLimitReached
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                              }
                            >
                              {item.statusText}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetDeviceStats(item.deviceId, (item as any).campaignId?._id || (item as any).campaignId)}
                              className="text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              title="Reset view and click counts for this device & campaign"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" /> Reset
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
